import Table, { SelectColumnNoSelectAll } from "components/Table/Table";
import type { SECTION_TYPE } from "constants/SettingsConstants";
import { commonColumnProps } from "constants/TableConstants";
import { useEffect } from "react";
import type { CellClickArgs } from "react-data-grid";
import { SELECT_COLUMN_KEY } from "react-data-grid";
import { useNavigate } from "react-router-dom";
import { setSelectedIds } from "reducers/future-medical";
import { setIsLoading } from "reducers/main";
import { useGetCostProjectionsQuery } from "services/cost-projection-api";
import type {
  ClaimantType,
  ClientType,
  CostProjectionDataType,
  FileType,
} from "types/DataTypes";
import { useToken } from "utils/context";
import { useQuery } from "utils/hooks";
import { useDispatch } from "utils/redux";
import { GLOBAL_SEARCH_PARAM } from "utils/useUrlStore";

export interface CostProjectionType {
  id: string;
  service_type: SECTION_TYPE;
  client: ClientType;
  created: string;
  claimant: ClaimantType;
  claim_numbers: { number: string }[];
  file: FileType;
  status: string;
  workspace_url: string;
}

export type CostProjectionTableDataType = {
  file_id: string;
  id: string;
  type: SECTION_TYPE;
  client_name: string;
  created: string;
  claimant_name: string;
  claim_numbers: string;
  status: string;
  workspace_url: string;
};

const CostProjectionTable = ({
  serviceType,
}: {
  serviceType: "mcp" | "lcp";
}) => {
  const { accessToken } = useToken();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const query = useQuery();

  const { data: projections, isLoading } = useGetCostProjectionsQuery(
    {
      type: serviceType.toUpperCase(),
      searchTerm: query.get(GLOBAL_SEARCH_PARAM),
    },
    { skip: !accessToken },
  );

  useEffect(() => {
    dispatch(setIsLoading(isLoading));
  }, [isLoading, dispatch]);

  const handleCellClick = (
    args: CellClickArgs<CostProjectionTableDataType>,
  ) => {
    // the checkbox column selects the row; it should not open the workspace
    if (args.column.key === SELECT_COLUMN_KEY) return;
    const workspaceUrl = args.row.workspace_url;
    navigate(workspaceUrl);
  };

  const handleSelectedRowsChange = (newSelectedRows: Set<string | number>) => {
    if (newSelectedRows.size > 1) {
      const lastSelected = Array.from(newSelectedRows).pop()!;
      setSelectedIds([lastSelected]);
      dispatch(setSelectedIds([lastSelected]));
    } else {
      setSelectedIds(Array.from(newSelectedRows));
      dispatch(setSelectedIds(Array.from(newSelectedRows)));
    }
  };

  const rows: CostProjectionTableDataType[] = [];

  projections?.results?.forEach((projection: CostProjectionDataType) => {
    const row: CostProjectionTableDataType = {
      file_id: projection.file.id.toString(),
      id: projection.id.toString(),
      type: projection.type as SECTION_TYPE,
      client_name: projection.file.client.name,
      created: new Date(projection.created).toLocaleDateString(),
      claimant_name: `${projection.claimant.first_name} ${projection.claimant.last_name}`,
      claim_numbers: projection.file.claim_numbers
        .map((cn) => cn.number)
        .join(", "),
      status:
        (projection as CostProjectionDataType & { status?: string }).status ||
        "", // status might be missing in DataType but present in API
      workspace_url: projection.workspace_url,
    };
    rows.push(row);
  });

  const COLUMNS = [
    SelectColumnNoSelectAll,
    { key: "id", name: "ID", ...commonColumnProps },
    { key: "type", name: "Type", ...commonColumnProps },
    { key: "client_name", name: "Client", ...commonColumnProps },
    { key: "created", name: "Created Date", ...commonColumnProps },
    { key: "claimant_name", name: "Claimant", ...commonColumnProps },
    { key: "claim_numbers", name: "Claim Numbers", ...commonColumnProps },
    { key: "status", name: "Status", ...commonColumnProps },
  ];

  return (
    <Table
      columns={COLUMNS}
      rows={rows}
      rowKeyGetter={(row: CostProjectionTableDataType) => row.id}
      onCellClick={handleCellClick}
      onSelectedRowsChange={handleSelectedRowsChange}
    />
  );
};

export default CostProjectionTable;
